import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { refreshPortfolioAndAssets } from "../services/digitalWalletService";
import {
  createZar8Unlock,
  createZar8UnlockQuote,
} from "../services/zar8YieldService";
import {
  emitZar8UnlockFlowEvent,
  ZAR8_LOCK_FLOW_STEPS,
} from "../utils/zar8LockFlow";

const PROCESSING_STEP_DELAY_MS = 1600;

const invalidateYieldQueries = (queryClient) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: ["availableBalances"] }),
    queryClient.invalidateQueries({ queryKey: ["ledgerEntries"] }),
    queryClient.invalidateQueries({ queryKey: ["pendingLedgers"] }),
    queryClient.invalidateQueries({ queryKey: ["investPortfolio"] }),
    queryClient.invalidateQueries({ queryKey: ["portfolioEstimateAssets"] }),
    queryClient.invalidateQueries({ queryKey: ["userTransactionHistory"] }),
    queryClient.invalidateQueries({ queryKey: ["zar8YieldPositions"] }),
    queryClient.invalidateQueries({ queryKey: ["zar8YieldHistory"] }),
    queryClient.invalidateQueries({ queryKey: ["zar8YieldEarnings"] }),
  ]);

const getApiErrorMessage = (error, fallbackMessage) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallbackMessage;

const isTwoFactorError = (error) => {
  const status = error?.response?.status;
  const message = getApiErrorMessage(error, "").toLowerCase();

  return (
    status === 401 || message.includes("2fa") || message.includes("two-factor")
  );
};

export default function useZar8UnlockFlow({
  amountValue,
  flowState,
  lockedBalance,
  onClose,
  onFlowStateChange,
  onModeChange,
  selectedUnlockPosition,
  toast,
  unlockQuote,
  setUnlockQuote,
}) {
  const queryClient = useQueryClient();
  const successTimeoutRef = useRef(null);
  const [amountError, setAmountError] = useState("");
  const [summaryError, setSummaryError] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isConfirmingUnlock, setIsConfirmingUnlock] = useState(false);
  const [isTermPickerOpen, setIsTermPickerOpen] = useState(false);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [showTwoFactorSetup, setShowTwoFactorSetup] = useState(false);
  const [twoFactorReadyOverride, setTwoFactorReadyOverride] = useState(false);

  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        window.clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  const updateFlowState = (updater) => {
    onFlowStateChange((currentState) => {
      const nextState =
        typeof updater === "function" ? updater(currentState) : updater;

      return {
        ...currentState,
        ...nextState,
      };
    });
  };

  const quoteMutation = useMutation({
    mutationFn: createZar8UnlockQuote,
    onError: (error) => {
      const message = error?.message || "We couldn’t prepare this unlock.";
      setSummaryError(message);
      toast({
        description: message,
        variant: "eighty",
      });
    },
  });

  const submitUnlockMutation = useMutation({
    mutationFn: createZar8Unlock,
    onSuccess: async (_, variables) => {
      await refreshPortfolioAndAssets();
      await invalidateYieldQueries(queryClient);

      successTimeoutRef.current = window.setTimeout(() => {
        emitZar8UnlockFlowEvent("zar8_unlock_success", {
          amount: variables.amountZar,
          termId: variables.selectedPosition?.termId,
          ledgerId: variables.selectedPosition?.ledgerId,
        });
        onFlowStateChange((currentState) => ({
          ...currentState,
          step: ZAR8_LOCK_FLOW_STEPS.success,
        }));
      }, PROCESSING_STEP_DELAY_MS);
    },
    onError: (error) => {
      emitZar8UnlockFlowEvent("zar8_unlock_failure", {
        reason: "unlock_submission_failed",
        message: error?.message || "Unknown unlock failure",
      });

      if (isTwoFactorError(error)) {
        setOtpError("We couldn’t verify that code. Try again.");
      } else {
        setSummaryError("We couldn’t complete your unlock request. Try again.");
      }

      setOtpCode("");
      onFlowStateChange((currentState) => ({
        ...currentState,
        step: ZAR8_LOCK_FLOW_STEPS.twoFactor,
      }));
      toast({
        description: getApiErrorMessage(
          error,
          "We couldn’t complete your unlock request."
        ),
        variant: "eighty",
      });
    },
  });

  const resetForMode = (nextFlowType) => {
    if (nextFlowType === flowState.flowType) {
      return;
    }

    onModeChange?.(nextFlowType);
  };

  const handleBack = () => {
    setAmountError("");
    setSummaryError("");
    setOtpError("");
    setIsConfirmationModalOpen(false);

    if (flowState.step === ZAR8_LOCK_FLOW_STEPS.amount) {
      onClose();
      return;
    }

    if (flowState.step === ZAR8_LOCK_FLOW_STEPS.summary) {
      updateFlowState({ step: ZAR8_LOCK_FLOW_STEPS.amount });
      return;
    }

    if (flowState.step === ZAR8_LOCK_FLOW_STEPS.twoFactor) {
      if (showTwoFactorSetup) {
        setShowTwoFactorSetup(false);
        return;
      }

      updateFlowState({ step: ZAR8_LOCK_FLOW_STEPS.summary });
    }
  };

  const handleAmountContinue = () => {
    if (!selectedUnlockPosition) {
      setAmountError(
        "You don’t have an active yield term available to unlock."
      );
      return;
    }

    if (amountValue <= 0) {
      setAmountError("Enter a valid amount to continue.");
      return;
    }

    if (amountValue > lockedBalance) {
      setAmountError("Amount exceeds your locked ZAR8 balance.");
      return;
    }

    setAmountError("");
    setSummaryError("");
    setUnlockQuote(null);
    setHasAcceptedTerms(false);
    emitZar8UnlockFlowEvent("zar8_unlock_initiated", {
      amount: amountValue,
      termId: selectedUnlockPosition.termId,
      ledgerId: selectedUnlockPosition.ledgerId,
    });
    updateFlowState({ step: ZAR8_LOCK_FLOW_STEPS.summary });
  };

  const handleSummaryContinue = async () => {
    if (!hasAcceptedTerms) {
      setSummaryError("Accept the Terms and Conditions to continue.");
      return;
    }

    setSummaryError("");
    try {
      const quote = await quoteMutation.mutateAsync({
        amountZar: amountValue,
        selectedPosition: selectedUnlockPosition,
      });
      setUnlockQuote(quote);
      setIsConfirmationModalOpen(true);
    } catch {
      // The mutation onError handler owns the inline message and toast.
    }
  };

  const handleConfirmUnlock = async () => {
    try {
      setIsConfirmingUnlock(true);
      emitZar8UnlockFlowEvent("zar8_unlock_confirmed", {
        amount: amountValue,
        termId: selectedUnlockPosition?.termId,
        ledgerId: selectedUnlockPosition?.ledgerId,
      });
      setIsConfirmationModalOpen(false);
      updateFlowState({ step: ZAR8_LOCK_FLOW_STEPS.twoFactor });
    } finally {
      setIsConfirmingUnlock(false);
    }
  };

  const handleOtpVerify = async () => {
    if (otpCode.length !== 6) {
      setOtpError("Enter the 6-digit verification code to continue.");
      return;
    }

    try {
      setIsVerifyingOtp(true);
      setOtpError("");
      setSummaryError("");
      updateFlowState({ step: ZAR8_LOCK_FLOW_STEPS.processing });
      submitUnlockMutation.mutate({
        amountZar: amountValue,
        selectedPosition: selectedUnlockPosition,
        twoFactorToken: otpCode,
        quote: unlockQuote,
      });
    } catch (error) {
      emitZar8UnlockFlowEvent("zar8_unlock_failure", {
        reason: "two_factor_verification_failed",
      });
      setOtpError("We couldn’t verify that code. Try again.");
      toast({
        description: `${error}`,
        variant: "eighty",
      });
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleClose = () => {
    emitZar8UnlockFlowEvent("zar8_unlock_abandoned", {
      step: flowState.step,
    });
    onClose();
  };

  const handleAmountInputChange = (value) => {
    setAmountError("");
    setSummaryError("");
    updateFlowState({
      amountInput: value,
    });
  };

  const handleMaxAmount = () => {
    setAmountError("");
    setSummaryError("");
    updateFlowState({
      amountInput: lockedBalance.toFixed(2),
    });
  };

  const handlePositionSelect = (positionId) => {
    setAmountError("");
    setSummaryError("");
    setUnlockQuote(null);
    updateFlowState({
      selectedUnlockPositionId: positionId,
      amountInput: "",
    });
  };

  return {
    amountError,
    handleAmountContinue,
    handleAmountInputChange,
    handleBack,
    handleClose,
    handleConfirmUnlock,
    handleMaxAmount,
    handleOtpVerify,
    handlePositionSelect,
    handleSummaryContinue,
    hasAcceptedTerms,
    isConfirmationModalOpen,
    isConfirmingUnlock,
    isQuoteLoading: quoteMutation.isPending,
    isSubmittingUnlock: submitUnlockMutation.isPending,
    isTermPickerOpen,
    isVerifyingOtp,
    otpCode,
    otpError,
    resetForMode,
    setHasAcceptedTerms,
    setIsConfirmationModalOpen,
    setIsTermPickerOpen,
    setOtpCode,
    setOtpError,
    setShowTwoFactorSetup,
    setSummaryError,
    setTwoFactorReadyOverride,
    showTwoFactorSetup,
    summaryError,
    twoFactorReadyOverride,
    updateFlowState,
  };
}
